import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';

const MyAlera = () => {
  const [activeTab, setActiveTab] = useState('favorites');
  const [savedRecipes] = useState([
    {
      id: 1,
      title: 'Coconut Chicken Curry',
      category: 'Dinner',
      allergies: ['Dairy-free', 'Gluten-free'],
      image: '🍛',
      savedDate: '2 days ago',
      cookTime: '30 mins',
      difficulty: 'Easy'
    },
    {
      id: 2,
      title: 'Anti-inflammatory Smoothie Bowl',
      category: 'Breakfast',
      allergies: ['Dairy-free', 'Nut-free'],
      image: '🥣',
      savedDate: '1 week ago',
      cookTime: '10 mins',
      difficulty: 'Easy'
    },
    {
      id: 3,
      title: 'Healing Turmeric Latte',
      category: 'Beverages',
      allergies: ['Dairy-free', 'Caffeine-free'],
      image: '☕',
      savedDate: '3 days ago',
      cookTime: '5 mins',
      difficulty: 'Easy'
    }
  ]);

  const [savedWorkouts] = useState([
    {
      id: 1,
      title: 'Gentle Morning Yoga',
      category: 'Yoga',
      duration: '20 mins',
      difficulty: 'Beginner',
      completedTimes: 5,
      lastCompleted: '1 day ago',
      image: '🧘‍♀️'
    },
    {
      id: 2,
      title: 'Stress Relief Stretches',
      category: 'Stretching',
      duration: '15 mins',
      difficulty: 'Beginner',
      completedTimes: 3,
      lastCompleted: '3 days ago',
      image: '🤸‍♀️'
    },
    {
      id: 3,
      title: 'Low-Impact Cardio',
      category: 'Cardio',
      duration: '25 mins',
      difficulty: 'Intermediate',
      completedTimes: 2,
      lastCompleted: '1 week ago',
      image: '🚶‍♀️'
    }
  ]);

  const [progressStats] = useState({
    totalWorkouts: 12,
    currentStreak: 7,
    longestStreak: 14,
    favoriteRecipes: 8,
    recipesToTry: 5,
    monthlyGoalProgress: 85,
    weeklyGoalProgress: 60
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const tabs = [
    { id: 'favorites', name: 'My Favorites', icon: '⭐' },
    { id: 'progress', name: 'Progress', icon: '📊' },
    { id: 'goals', name: 'Goals', icon: '🎯' },
    { id: 'achievements', name: 'Achievements', icon: '🏆' }
  ];

  const achievements = [
    {
      id: 1,
      title: 'First Week Complete',
      description: 'Completed your first 7-day workout streak',
      icon: '🔥',
      earned: true,
      earnedDate: '1 week ago'
    },
    {
      id: 2,
      title: 'Recipe Explorer',
      description: 'Tried 5 new allergy-friendly recipes',
      icon: '👩‍🍳',
      earned: true,
      earnedDate: '3 days ago'
    },
    {
      id: 3,
      title: 'Community Helper',
      description: 'Shared helpful tips in the forum',
      icon: '🤝',
      earned: true,
      earnedDate: '5 days ago'
    },
    {
      id: 4,
      title: 'Consistency Champion',
      description: 'Complete 30 workouts in 60 days',
      icon: '💪',
      earned: false,
      progress: 12,
      target: 30
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 via-beige-50 to-sage-100 pt-20 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-3">
            <RouterLink
              to="/dashboard"
              className="text-gray-600 hover:text-gray-800 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </RouterLink>
            <h1 className="text-3xl font-playfair font-semibold text-charcoal-900">My Alera</h1>
          </div>
          <div className="text-sm text-charcoal-600">
            Your personal wellness collection
          </div>
        </div>

        {/* Tabs */}
        <div className="flex space-x-1 mb-8 bg-white/60 backdrop-blur-sm rounded-2xl p-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-xl font-medium transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-sage-600 text-white shadow-soft'
                  : 'text-charcoal-600 hover:bg-white/50'
              }`}
            >
              <span>{tab.icon}</span>
              <span className="hidden sm:inline">{tab.name}</span>
            </button>
          ))}
        </div>

        {/* Favorites Tab */}
        {activeTab === 'favorites' && (
          <div className="space-y-8">
            {/* Saved Recipes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-soft"
            >
              <h2 className="text-xl font-semibold text-charcoal-900 mb-4 flex items-center">
                <span className="mr-2">🍽️</span>
                Saved Recipes ({savedRecipes.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {savedRecipes.map((recipe) => (
                  <div key={recipe.id} className="rounded-2xl p-4 border" style={{ background: 'linear-gradient(to bottom right, #e8ebe7, #d4ddd0)', borderColor: '#c8d4c0' }}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-3xl">{recipe.image}</span>
                      <button className="text-red-500 hover:text-red-600">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                    <h3 className="font-semibold text-charcoal-800 mb-2">{recipe.title}</h3>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {recipe.allergies.map((allergy) => (
                        <span key={allergy} className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: '#e8ebe7', color: '#a8b5a0' }}>
                          {allergy}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between text-sm text-charcoal-600">
                      <span>{recipe.cookTime}</span>
                      <span>Saved {recipe.savedDate}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Saved Workouts */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-soft"
            >
              <h2 className="text-xl font-semibold text-charcoal-900 mb-4 flex items-center">
                <span className="mr-2">💪</span>
                Favorite Workouts ({savedWorkouts.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {savedWorkouts.map((workout) => (
                  <div key={workout.id} className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-4 border border-blue-100">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-3xl">{workout.image}</span>
                      <div className="text-right">
                        <div className="text-sm font-medium text-blue-700">{workout.completedTimes}x</div>
                        <div className="text-xs text-blue-600">completed</div>
                      </div>
                    </div>
                    <h3 className="font-semibold text-charcoal-800 mb-2">{workout.title}</h3>
                    <div className="flex items-center justify-between text-sm text-charcoal-600 mb-2">
                      <span>{workout.duration}</span>
                      <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs">{workout.difficulty}</span>
                    </div>
                    <div className="text-xs text-charcoal-500">
                      Last completed {workout.lastCompleted}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        )}

        {/* Progress Tab */}
        {activeTab === 'progress' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Progress Overview */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 text-center">
                <div className="text-2xl font-bold text-blue-600 mb-1">{progressStats.totalWorkouts}</div>
                <div className="text-sm text-charcoal-600">Total Workouts</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 text-center">
                <div className="text-2xl font-bold text-orange-600 mb-1 flex items-center justify-center">
                  🔥 {progressStats.currentStreak}
                </div>
                <div className="text-sm text-charcoal-600">Current Streak</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 text-center">
                <div className="text-2xl font-bold mb-1" style={{ color: '#a8b5a0' }}>{progressStats.favoriteRecipes}</div>
                <div className="text-sm text-charcoal-600">Favorite Recipes</div>
              </div>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 text-center">
                <div className="text-2xl font-bold text-purple-600 mb-1">{progressStats.longestStreak}</div>
                <div className="text-sm text-charcoal-600">Longest Streak</div>
              </div>
            </div>

            {/* Goal Progress */}
            <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-soft">
              <h3 className="text-xl font-semibold text-charcoal-900 mb-4">Goal Progress</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-charcoal-700">Monthly Fitness Goal</span>
                    <span className="text-sm text-charcoal-600">{progressStats.monthlyGoalProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-300"
                      style={{width: `${progressStats.monthlyGoalProgress}%`}}
                    ></div>
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-charcoal-700">Weekly Recipe Goal</span>
                    <span className="text-sm text-charcoal-600">{progressStats.weeklyGoalProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="h-2 rounded-full transition-all duration-300"
                      style={{ backgroundColor: '#a8b5a0', width: `${progressStats.weeklyGoalProgress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Achievements Tab */}
        {activeTab === 'achievements' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 shadow-soft"
          >
            <h2 className="text-xl font-semibold text-charcoal-900 mb-6">Your Achievements</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {achievements.map((achievement) => (
                <div 
                  key={achievement.id} 
                  className={`rounded-2xl p-4 border-2 ${
                    achievement.earned 
                      ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200' 
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <span className={`text-2xl ${achievement.earned ? '' : 'grayscale opacity-50'}`}>
                      {achievement.icon}
                    </span>
                    <div>
                      <h3 className={`font-semibold ${achievement.earned ? 'text-charcoal-800' : 'text-gray-500'}`}>
                        {achievement.title}
                      </h3>
                      {achievement.earned && (
                        <div className="text-xs text-orange-600">Earned {achievement.earnedDate}</div>
                      )}
                    </div>
                  </div>
                  <p className={`text-sm ${achievement.earned ? 'text-charcoal-600' : 'text-gray-500'}`}>
                    {achievement.description}
                  </p>
                  {!achievement.earned && achievement.progress && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-600">Progress</span>
                        <span className="text-xs text-gray-600">{achievement.progress}/{achievement.target}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                        <div 
                          className="bg-gradient-to-r from-blue-500 to-purple-500 h-1.5 rounded-full"
                          style={{width: `${(achievement.progress / achievement.target) * 100}%`}}
                        ></div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default MyAlera;
